import React from 'react';
import {
  Code as CodeIcon,
  Description as DescriptionIcon,
  Shield as ShieldIcon,
  Refresh as RefreshIcon,
  InsertDriveFile as FileIcon,
  Build as BuildIcon,
  Key as KeyIcon,
  AppRegistration as AppRegistrationIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
  Person as PersonIcon,
  SmartToy as SmartToyIcon,
  Token as TokenIcon,
} from '@mui/icons-material';

/**
 * The icon set a launcher tile can render, shared by the tile grid and by the two editors that
 * let someone pick one (Manage Team → Tools & Resources, and the Deflection Catalog admin page).
 *
 * Kept in one place on purpose: the server validates the chosen key against the same list
 * (DeflectionTilePolicy.Icons in Intake/Deflection/DeflectionModels.cs), and a key that exists on
 * one side but not the other either rejects a legitimate choice or renders a blank tile. Adding an
 * icon means adding it to BOTH lists.
 */
export const TILE_ICON_SIZE = 36;

export const iconMap: Record<string, React.ReactNode> = {
  Code: <CodeIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
  Description: <DescriptionIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
  Shield: <ShieldIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
  Refresh: <RefreshIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
  File: <FileIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
  Build: <BuildIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
  Key: <KeyIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
  AppRegistration: <AppRegistrationIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
  RestoreFromTrash: <RestoreFromTrashIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
  Person: <PersonIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
  SmartToy: <SmartToyIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
  Token: <TokenIcon sx={{ fontSize: TILE_ICON_SIZE }} />,
};

/** The icon used when none is chosen, or when a stored key is no longer recognised. */
export const DEFAULT_TILE_ICON = 'Build';

/** Ordered keys for a picker. Derived from {@link iconMap} so the two cannot drift. */
export const TILE_ICON_KEYS: string[] = Object.keys(iconMap);

/** Renders an icon at picker scale rather than tile scale. */
export const renderPickerIcon = (key: string): React.ReactNode => {
  const node = iconMap[key] ?? iconMap[DEFAULT_TILE_ICON];
  return React.isValidElement(node)
    ? React.cloneElement(node as React.ReactElement<{ sx?: object }>, { sx: { fontSize: 24 } })
    : node;
};
